import axios from 'axios';

import { getAuthToken } from '../utils/authUtils'; //

const API_BASE = process.env.REACT_APP_FIREBASE_BACKEND_API_BASE || 'https://quantumleap-emr-dev.web.app';


// Types for API integrations (matching backend)
export interface APIIntegration {
  id?: string;
  name: string;
  description: string;
  type: 'REST' | 'SOAP' | 'GraphQL' | 'WebSocket' | 'FTP' | 'SFTP';
  category: 'Healthcare' | 'Insurance' | 'Laboratory' | 'Pharmacy' | 'Billing' | 'EHR' | 'Other';
  baseUrl: string;
  version?: string;
  active: boolean;
  
  // Authentication
  authType: 'none' | 'basic' | 'bearer' | 'api_key' | 'oauth2' | 'certificate';
  authConfig: {
    username?: string;
    password?: string;
    token?: string;
    apiKey?: string;
    apiKeyHeader?: string;
    clientId?: string;
    clientSecret?: string;
    tokenUrl?: string;
    scope?: string;
    certificatePath?: string;
    certificatePassword?: string;
  };
  
  // Configuration
  timeout?: number;
  retryAttempts?: number;
  retryDelay?: number;
  rateLimit?: {
    requests: number;
    window: number; // in milliseconds
  };
  
  // Metadata
  facilityId?: string;
  createdAt?: string;
  updatedAt?: string;
  createdBy?: string;
  lastTested?: string;
  testResults?: any;
}

/**
 * Secure API Integrations Service
 * Uses backend API endpoints instead of direct Firestore access
 */

/**
 * Get all API integrations with optional filtering
 * @param filters - Filters (facilityId, category, type, active)
 * @param page - Page number
 * @param limit - Items per page
 * @returns API integrations with pagination
 */
export async function getAPIIntegrations(
  filters: Record<string, any> = {}, 
  page: number = 1, 
  limit: number = 50
): Promise<{ integrations: APIIntegration[]; pagination: any }> {
  try {
    console.log('Fetching API integrations with filters:', filters);
    const token = await getAuthToken();
    
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('limit', limit.toString());
    
    // Add filters
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value.toString());
      }
    });
    
    const response = await axios.get(
      `${API_BASE}/integrations/api-integrations?${params.toString()}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return {
      integrations: response.data.integrations,
      pagination: response.data.pagination
    };
  } catch (error) {
    console.error('Error fetching API integrations:', error);
    throw error;
  }
}

/**
 * Get a specific API integration by ID
 * @param integrationId - Integration ID
 * @returns API integration object
 */
export async function getAPIIntegrationById(integrationId: string): Promise<APIIntegration | null> {
  try {
    console.log('Fetching API integration by ID:', integrationId);
    const token = await getAuthToken();
    
    const response = await axios.get(
      `${API_BASE}/integrations/api-integrations/${integrationId}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data.integration;
  } catch (error) {
    console.error('Error fetching API integration by ID:', error);
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return null;
    }
    throw error;
  }
}

/**
 * Create a new API integration (admin only)
 * @param integrationData - Integration data
 * @returns Created integration
 */
export async function createAPIIntegration(integrationData: Partial<APIIntegration>): Promise<APIIntegration> {
  try {
    console.log('Creating API integration:', integrationData.name);
    const token = await getAuthToken();
    
    const response = await axios.post(
      `${API_BASE}/integrations/api-integrations`,
      integrationData,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error creating API integration:', error);
    throw error;
  }
}

/**
 * Update an existing API integration (admin only)
 * @param integrationId - Integration ID
 * @param updateData - Data to update
 * @returns Update result
 */
export async function updateAPIIntegration(integrationId: string, updateData: Partial<APIIntegration>): Promise<any> {
  try {
    console.log('Updating API integration:', integrationId);
    const token = await getAuthToken();
    
    const response = await axios.put(
      `${API_BASE}/integrations/api-integrations/${integrationId}`,
      updateData,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error updating API integration:', error);
    throw error;
  }
}

/**
 * Delete an API integration (admin only)
 * @param integrationId - Integration ID
 * @returns Deletion result
 */
export async function deleteAPIIntegration(integrationId: string): Promise<any> {
  try {
    console.log('Deleting API integration:', integrationId);
    const token = await getAuthToken();
    
    const response = await axios.delete(
      `${API_BASE}/integrations/api-integrations/${integrationId}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error deleting API integration:', error);
    throw error;
  }
}

/**
 * Test an API integration connectivity (admin only)
 * @param integrationId - Integration ID
 * @returns Test result
 */
export async function testAPIIntegration(integrationId: string): Promise<any> {
  try {
    console.log('Testing API integration:', integrationId);
    const token = await getAuthToken();
    
    const response = await axios.post(
      `${API_BASE}/integrations/api-integrations/${integrationId}/test`,
      {},
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data.testResult;
  } catch (error) {
    console.error('Error testing API integration:', error);
    throw error;
  }
}

/**
 * Get API integrations by category
 * @param category - Integration category
 * @param activeOnly - Whether to include only active integrations
 * @returns Array of integrations
 */
export async function getAPIIntegrationsByCategory(
  category: string, 
  activeOnly: boolean = true
): Promise<APIIntegration[]> {
  try {
    const filters = {
      category,
      active: activeOnly ? 'true' : undefined
    };
    
    const result = await getAPIIntegrations(filters, 1, 100);
    return result.integrations;
  } catch (error) {
    console.error('Error fetching API integrations by category:', error);
    throw error;
  }
}

/**
 * Get API integrations by type
 * @param type - Integration type
 * @param activeOnly - Whether to include only active integrations
 * @returns Array of integrations
 */
export async function getAPIIntegrationsByType(
  type: string, 
  activeOnly: boolean = true
): Promise<APIIntegration[]> {
  try {
    const filters = {
      type,
      active: activeOnly ? 'true' : undefined
    };
    
    const result = await getAPIIntegrations(filters, 1, 100);
    return result.integrations;
  } catch (error) {
    console.error('Error fetching API integrations by type:', error);
    throw error;
  }
}

/**
 * Get API integrations for a facility
 * @param facilityId - Facility ID
 * @param activeOnly - Whether to include only active integrations
 * @returns Array of integrations
 */
export async function getFacilityAPIIntegrations(
  facilityId: string, 
  activeOnly: boolean = true
): Promise<APIIntegration[]> {
  try {
    const filters = {
      facilityId,
      active: activeOnly ? 'true' : undefined
    };
    
    const result = await getAPIIntegrations(filters, 1, 100);
    return result.integrations;
  } catch (error) {
    console.error('Error fetching facility API integrations:', error);
    throw error;
  }
}

/**
 * Execute a request through an API integration
 * @param integrationId - Integration ID
 * @param endpoint - API endpoint to call
 * @param method - HTTP method
 * @param data - Request data
 * @returns API response
 */
export async function executeAPIIntegrationRequest(
  integrationId: string,
  endpoint: string,
  method: string = 'GET',
  data?: any
): Promise<any> {
  try {
    console.log('Executing API integration request:', integrationId, endpoint);
    const token = await getAuthToken();
    
    const response = await axios.post(
      `${API_BASE}/integrations/api-integrations/${integrationId}/execute`,
      {
        endpoint,
        method,
        data
      },
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error executing API integration request:', error);
    throw error;
  }
}

/**
 * Get integration usage statistics
 * @param integrationId - Integration ID
 * @param dateRange - Date range for statistics
 * @returns Usage statistics
 */
export async function getAPIIntegrationUsageStats(
  integrationId: string,
  dateRange: { startDate: string; endDate: string }
): Promise<any> {
  try {
    console.log('Fetching API integration usage stats:', integrationId);
    const token = await getAuthToken();
    
    const params = new URLSearchParams();
    params.append('startDate', dateRange.startDate);
    params.append('endDate', dateRange.endDate);
    
    const response = await axios.get(
      `${API_BASE}/integrations/api-integrations/${integrationId}/stats?${params.toString()}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data.stats;
  } catch (error) {
    console.error('Error fetching API integration usage stats:', error);
    throw error;
  }
}

/**
 * Get available API types
 * @returns Array of API types
 */
export function getAPITypes(): string[] {
  return ['REST', 'SOAP', 'GraphQL', 'WebSocket', 'FTP', 'SFTP'];
}

/**
 * Get available API categories
 * @returns Array of API categories
 */
export function getAPICategories(): string[] {
  return ['Healthcare', 'Insurance', 'Laboratory', 'Pharmacy', 'Billing', 'EHR', 'Other'];
}

/**
 * Get available integration categories (alias for getAPICategories)
 * @returns Array of integration categories
 */
export function getIntegrationCategories(): string[] {
  return getAPICategories();
}

/**
 * Get available authentication types
 * @returns Array of authentication types
 */
export function getAuthTypes(): string[] {
  return ['none', 'basic', 'bearer', 'api_key', 'oauth2', 'certificate'];
}

/**
 * Get available authentication types (alias for getAuthTypes)
 * @returns Array of authentication types
 */
export function getAuthenticationTypes(): string[] {
  return getAuthTypes();
}

/**
 * Get all API integrations (alias for getAPIIntegrations with default parameters)
 * @returns Array of all integrations
 */
export async function getAllIntegrations(): Promise<APIIntegration[]> {
  try {
    const result = await getAPIIntegrations({}, 1, 1000); // Get first 1000 integrations
    return result.integrations;
  } catch (error) {
    console.error('Error getting all integrations:', error);
    throw error;
  }
}

/**
 * Create integration (alias for createAPIIntegration)
 * @param integrationData - Integration data
 * @returns Created integration
 */
export async function createIntegration(integrationData: Partial<APIIntegration>): Promise<APIIntegration> {
  return await createAPIIntegration(integrationData);
}

/**
 * Update integration (alias for updateAPIIntegration)
 * @param integrationId - Integration ID
 * @param updateData - Data to update
 * @returns Update result
 */
export async function updateIntegration(integrationId: string, updateData: Partial<APIIntegration>): Promise<any> {
  return await updateAPIIntegration(integrationId, updateData);
}

/**
 * Delete integration (alias for deleteAPIIntegration)
 * @param integrationId - Integration ID
 * @returns Deletion result
 */
export async function deleteIntegration(integrationId: string): Promise<any> {
  return await deleteAPIIntegration(integrationId);
}

/**
 * Toggle integration status (enable/disable)
 * @param integrationId - Integration ID
 * @returns Update result
 */
export async function toggleIntegrationStatus(integrationId: string): Promise<any> {
  try {
    // First get the current integration to check its status
    const integration = await getAPIIntegrationById(integrationId);
    if (!integration) {
      throw new Error('Integration not found');
    }
    
    // Toggle the active status
    const newStatus = !integration.active;
    return await updateAPIIntegration(integrationId, { active: newStatus });
  } catch (error) {
    console.error('Error toggling integration status:', error);
    throw error;
  }
}

/**
 * Test connection for an API integration
 * @param integrationId - Integration ID
 * @param testType - Type of test (connectivity, authentication, endpoint, full)
 * @param userId - User ID performing the test
 * @returns Test result
 */
export async function testConnection(integrationId: string, testType: string = 'connectivity', userId?: string): Promise<any> {
  try {
    console.log('Testing connection for integration:', integrationId, 'Test type:', testType);
    const token = await getAuthToken();
    
    const response = await axios.post(
      `${API_BASE}/integrations/api-integrations/${integrationId}/test`,
      {
        testType,
        userId
      },
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data.testResult;
  } catch (error) {
    console.error('Error testing connection:', error);
    throw error;
  }
}

/**
 * Get test history for an API integration
 * @param integrationId - Integration ID
 * @param limit - Number of test records to retrieve
 * @returns Array of test history records
 */
export async function getTestHistory(integrationId: string, limit: number = 10): Promise<any[]> {
  try {
    console.log('Getting test history for integration:', integrationId);
    const token = await getAuthToken();
    
    const params = new URLSearchParams();
    params.append('limit', limit.toString());
    
    const response = await axios.get(
      `${API_BASE}/integrations/api-integrations/${integrationId}/test-history?${params.toString()}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data.testHistory || [];
  } catch (error) {
    console.error('Error getting test history:', error);
    // Return empty array if test history endpoint doesn't exist or fails
    return [];
  }
}

/**
 * Validate API integration configuration locally (synchronous)
 * @param integrationData - Integration data to validate
 * @returns Local validation result
 */
export function validateIntegration(integrationData: Partial<APIIntegration>): { valid: boolean; errors: string[] } {
  const errors: string[] = [];
  
  // Required fields validation
  if (!integrationData.name || integrationData.name.trim() === '') {
    errors.push('Name is required');
  }
  
  if (!integrationData.baseUrl || integrationData.baseUrl.trim() === '') {
    errors.push('Base URL is required');
  }
  
  if (!integrationData.type) {
    errors.push('Type is required');
  }
  
  if (!integrationData.category) {
    errors.push('Category is required');
  }
  
  // URL format validation
  if (integrationData.baseUrl) {
    try {
      new URL(integrationData.baseUrl);
    } catch {
      errors.push('Base URL must be a valid URL');
    }
  }
  
  // Auth configuration validation
  if (integrationData.authType) {
    switch (integrationData.authType) {
      case 'basic':
        if (!integrationData.authConfig?.username) {
          errors.push('Username is required for basic authentication');
        }
        if (!integrationData.authConfig?.password) {
          errors.push('Password is required for basic authentication');
        }
        break;
      case 'bearer':
        if (!integrationData.authConfig?.token) {
          errors.push('Token is required for bearer authentication');
        }
        break;
      case 'api_key':
        if (!integrationData.authConfig?.apiKey) {
          errors.push('API Key is required for API key authentication');
        }
        break;
      case 'oauth2':
        if (!integrationData.authConfig?.clientId) {
          errors.push('Client ID is required for OAuth2 authentication');
        }
        if (!integrationData.authConfig?.clientSecret) {
          errors.push('Client Secret is required for OAuth2 authentication');
        }
        break;
    }
  }
  
  return {
    valid: errors.length === 0,
    errors
  };
}

/**
 * Validate API integration configuration (async backend validation)
 * @param integrationData - Integration data to validate
 * @returns Validation result
 */
export async function validateAPIIntegrationConfig(integrationData: Partial<APIIntegration>): Promise<any> {
  try {
    console.log('Validating API integration config');
    const token = await getAuthToken();
    
    const response = await axios.post(
      `${API_BASE}/integrations/api-integrations/validate`,
      integrationData,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error validating API integration config:', error);
    throw error;
  }
}

/**
 * Get available integration templates
 * @returns Array of integration templates
 */
export async function getAPIIntegrationTemplates(): Promise<any[]> {
  try {
    console.log('Fetching API integration templates');
    const token = await getAuthToken();
    
    const response = await axios.get(
      `${API_BASE}/integrations/api-integrations/templates`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data.templates;
  } catch (error) {
    console.error('Error fetching API integration templates:', error);
    // Return empty array if templates endpoint doesn't exist
    return [];
  }
}

/**
 * Clone an existing API integration
 * @param integrationId - Integration ID to clone
 * @param newName - Name for the cloned integration
 * @returns Cloned integration
 */
export async function cloneAPIIntegration(integrationId: string, newName: string): Promise<APIIntegration> {
  try {
    console.log('Cloning API integration:', integrationId);
    const token = await getAuthToken();
    
    const response = await axios.post(
      `${API_BASE}/integrations/api-integrations/${integrationId}/clone`,
      { newName },
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error cloning API integration:', error);
    throw error;
  }
}

/**
 * Export API integration configuration
 * @param integrationId - Integration ID
 * @returns Exported configuration
 */
export async function exportAPIIntegrationConfig(integrationId: string): Promise<any> {
  try {
    console.log('Exporting API integration config:', integrationId);
    const token = await getAuthToken();
    
    const response = await axios.get(
      `${API_BASE}/integrations/api-integrations/${integrationId}/export`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data.config;
  } catch (error) {
    console.error('Error exporting API integration config:', error);
    throw error;
  }
}

/**
 * Import API integration configuration
 * @param configData - Configuration data to import
 * @returns Imported integration
 */
export async function importAPIIntegrationConfig(configData: any): Promise<APIIntegration> {
  try {
    console.log('Importing API integration config');
    const token = await getAuthToken();
    
    const response = await axios.post(
      `${API_BASE}/integrations/api-integrations/import`,
      { config: configData },
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error importing API integration config:', error);
    throw error;
  }
}
